import React, { createContext, useContext, useEffect } from 'react';
import { useTenant } from './hooks/useTenant';
import { useCollection } from './hooks/useCollection';
import { getLookupData, setLookupData } from './lookupStore';
import {
  VEHICLE_TYPES, ASSET_TYPES, CQC_TYPES,
  AUTISTI_STATUS, VEICOLI_STATUS, IMPIANTI_STATUS, ASSET_STATUS, INTERVENTI_STATUS,
  CER_CODES, INDUSTRY_SECTOR, SENSOR_CATALOG,
} from '../data/catalogs';

const AppDataContext = createContext(null);

// Re-export per retrocompatibilità: il singleton di lookup vive ora in
// lookupStore.js (senza dipendenze React/Firebase). Vedi lookupStore.js.
export { getLookupData };

export function AppDataProvider({ children }) {
  const { activeTenantId } = useTenant();
  const tid = activeTenantId;

  const { data: ASSET_DATA, loading: _assetLoading } = useCollection(tid ? `tenants/${tid}/assets` : null, [], { raw: true });
  const VEICOLI_DATA              = useCollection(tid ? `tenants/${tid}/vehicles`         : null);
  const AUTISTI_DATA              = useCollection(tid ? `tenants/${tid}/drivers`          : null);
  const IMPIANTI_DATA             = useCollection(tid ? `tenants/${tid}/plants`           : null);
  const PRODUCER_DATA             = useCollection(tid ? `tenants/${tid}/producers`        : null);
  const LOCATIONS_DATA            = useCollection(tid ? `tenants/${tid}/locations`        : null);
  const RICHIESTE_INTERVENTO_DATA = useCollection(tid ? `tenants/${tid}/requests`         : null);
  const INTERVENTI_DATA           = useCollection(tid ? `tenants/${tid}/interventions`    : null);
  const INVOICES_DATA             = useCollection(tid ? `tenants/${tid}/invoices`         : null);
  const ROUTE_DATA                = useCollection(tid ? `tenants/${tid}/routes`           : null);
  const SENSOR_INSTANCES          = useCollection(tid ? `tenants/${tid}/sensorInstances`  : null);
  // Alert sensori aperti (campanella). Solo where status==open → niente indice
  // composito; l'ordinamento per data avviene client-side nella campanella.
  const ALERTS_DATA               = useCollection(tid ? `tenants/${tid}/alerts` : null, [{ field: 'status', op: '==', value: 'open' }]);

  const value = {
    loading: !!tid && _assetLoading,
    ASSET_DATA,
    VEICOLI_DATA,
    AUTISTI_DATA,
    IMPIANTI_DATA,
    PRODUCER_DATA,
    LOCATIONS_DATA,
    RICHIESTE_INTERVENTO_DATA,
    INTERVENTI_DATA,
    INVOICES_DATA,
    ROUTE_DATA,
    SENSOR_INSTANCES,
    ALERTS_DATA,
    // Cataloghi statici (bundled nel client — CER UE, settori, sensori)
    CER_CODES,
    SENSOR_CATALOG,
    INDUSTRY_SECTOR,
    // Enum statici legati alla business logic
    VEHICLE_TYPES,
    ASSET_TYPES,
    CQC_TYPES,
    AUTISTI_STATUS,
    VEICOLI_STATUS,
    IMPIANTI_STATUS,
    ASSET_STATUS,
    INTERVENTI_STATUS,
  };

  // Aggiorna il singleton di lookup ad ogni variazione dei dati
  useEffect(() => {
    setLookupData(value);
  });

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData deve essere usato dentro <AppDataProvider>');
  return ctx;
}
